import React, { createContext, useContext, useState, ReactNode } from 'react';

type IdeaStatus = 'Draft' | 'Pending' | 'Approved' | 'Completed' | 'Split' | 'Dropped' | 'TBD';

interface IdeaData {
  id: string;
  name: string;
  description: string | null;
  area: string | null;
  target_year?: number;
  affecting_metric?: string[];
  flagged_to_be_revisited?: boolean;
  task_status?: IdeaStatus | null;
}

interface EditIdeaDialogContextType {
  isOpen: boolean;
  idea: IdeaData | null;
  openEditIdeaDialog: (idea: IdeaData) => void;
  closeEditIdeaDialog: () => void;
  onSuccess?: () => void;
  setOnSuccess: (callback: () => void) => void;
}

const EditIdeaDialogContext = createContext<EditIdeaDialogContextType | undefined>(undefined);

interface EditIdeaDialogProviderProps {
  children: ReactNode;
}

export const EditIdeaDialogProvider = ({ children }: EditIdeaDialogProviderProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [idea, setIdea] = useState<IdeaData | null>(null);
  const [onSuccess, setOnSuccessCallback] = useState<(() => void) | undefined>();

  const openEditIdeaDialog = (ideaData: IdeaData) => {
    setIdea(ideaData);
    setIsOpen(true);
  };

  const closeEditIdeaDialog = () => {
    setIsOpen(false);
  };

  const setOnSuccess = (callback: () => void) => {
    setOnSuccessCallback(() => callback);
  };

  const value: EditIdeaDialogContextType = {
    isOpen,
    idea,
    openEditIdeaDialog,
    closeEditIdeaDialog,
    onSuccess,
    setOnSuccess,
  };

  return (
    <EditIdeaDialogContext.Provider value={value}>
      {children}
    </EditIdeaDialogContext.Provider>
  );
};

export const useEditIdeaDialog = () => {
  const context = useContext(EditIdeaDialogContext);
  if (context === undefined) {
    throw new Error('useEditIdeaDialog must be used within a EditIdeaDialogProvider');
  }
  return context;
};
